import type { IFeefoServiceReview, IFeefoSummary } from '@nuxt-monorepo/ui/types/seo/FeefoReviews';

const DEFAULT_LIMIT = 5;

function buildCacheKey(prefix: string, parts: (string | number)[]): string {
    return [prefix, ...parts].join('-');
}

function useFeefoReviewsBase(options: { limit?: number; productSku?: string }) {
    const { limit = DEFAULT_LIMIT, productSku } = options;

    const cacheKey = buildCacheKey('feefo-reviews', [productSku ? `product-${productSku}` : 'service', limit]);

    const { data, pending, error } = useAsyncData(
        cacheKey,
        () =>
            $fetch<{ reviews: IFeefoServiceReview[] }>('/api/feefo-reviews', {
                params: {
                    page_size: String(limit),
                    ...(productSku && { product_sku: productSku }),
                },
            }),
        { server: true }
    );

    const reviews = computed<IFeefoServiceReview[]>(() => data.value?.reviews ?? []);

    return { reviews, pending, error };
}

function useFeefoSummaryBase(options: { productSku?: string }) {
    const { productSku } = options;

    const cacheKey = buildCacheKey('feefo-summary', [productSku ? `product-${productSku}` : 'service']);

    const { data, pending, error } = useAsyncData(
        cacheKey,
        () =>
            $fetch<{ summary: IFeefoSummary | null }>('/api/feefo-summary', {
                params: productSku ? { product_sku: productSku } : {},
            }),
        { server: true }
    );

    const summary = computed<IFeefoSummary | null>(() => data.value?.summary ?? null);

    return { summary, pending, error };
}

export function useFeefoServiceReviews(limit = DEFAULT_LIMIT) {
    return useFeefoReviewsBase({ limit });
}

export function useFeefoProductReviews(productSku: string, limit = DEFAULT_LIMIT) {
    return useFeefoReviewsBase({ limit, productSku });
}

export function useFeefoServiceSummary() {
    return useFeefoSummaryBase({});
}

export function useFeefoProductSummary(productSku: string) {
    return useFeefoSummaryBase({ productSku });
}
